import React, {PropsWithChildren} from "react"
import {ApplicationStatus, Assignee, EvaluationOutcome} from "../../resources/application"
import {startCase} from "lodash"
import classNames from "classnames"
import moment from "moment"
import {ProductApplicationStatus} from "../../resources/productApplication"
import {UnifiedApplicationStatus} from "../../resources/unifiedApplication"
import {TagTheme} from "./Tag"

export function Tag({label, value, className}: {label: string; value: string; className?: string}) {
	const tagClassname = classNames("tag", className)

	return (
		<div className="tags has-addons is-medium">
			<span className="tag is-dark">{label}</span>
			<span className={tagClassname} data-chromatic="ignore">
				{value}
			</span>
		</div>
	)
}

export function Tags({children}: PropsWithChildren<{}>) {
	const wrapped = React.Children.map(children, (child) => <div className="control">{child}</div>)

	return <div className="field is-grouped is-grouped-multiline">{wrapped}</div>
}

export function productApplicationStatusTheme(status: ProductApplicationStatus): string {
	switch (status) {
		case ProductApplicationStatus.AwaitingDocuments:
		case ProductApplicationStatus.Pending:
		case ProductApplicationStatus.PendingSubmission:
			return "info"
		case ProductApplicationStatus.PendingReview:
			return "warning"
		case ProductApplicationStatus.Approved:
		case ProductApplicationStatus.PreApproved:
			return "success"
		case ProductApplicationStatus.Denied:
		case ProductApplicationStatus.Canceled:
			return "danger"
	}
}

export function ProductApplicationStatusTag({status}: {status: ProductApplicationStatus}) {
	return <Tag value={startCase(status)} label="Status" className={`is-${productApplicationStatusTheme(status)}`} />
}

export function unifiedApplicationStatusTheme(status: UnifiedApplicationStatus): TagTheme {
	switch (status) {
		case UnifiedApplicationStatus.Approved:
			return "success"
		case UnifiedApplicationStatus.Rejected:
		case UnifiedApplicationStatus.Error:
			return "error"
		case UnifiedApplicationStatus.AwaitingDocuments:
		case UnifiedApplicationStatus.AwaitingIdentityVerification:
		case UnifiedApplicationStatus.PartiallyApproved:
			return "warning"
		case UnifiedApplicationStatus.Submitted:
		case UnifiedApplicationStatus.InReview:
			return "info"
		case UnifiedApplicationStatus.Pending:
		case UnifiedApplicationStatus.Cancelled:
			return "neutral"
	}
}

const bulmaThemeByTagTheme: Record<TagTheme, string> = {
	success: "is-success",
	error: "is-danger",
	warning: "is-warning",
	info: "is-info",
	neutral: "is-white",
	dark: "is-dark",
}

export function UnifiedApplicationStatusTag({status}: {status: UnifiedApplicationStatus}) {
	return (
		<Tag
			value={startCase(status)}
			label="Status"
			className={bulmaThemeByTagTheme[unifiedApplicationStatusTheme(status)]}
		/>
	)
}

export function applicationStatusTheme(status: ApplicationStatus): string {
	switch (status) {
		case ApplicationStatus.AwaitingDocuments:
		case ApplicationStatus.AwaitingInformation:
		case ApplicationStatus.Pending:
			return "info"
		case ApplicationStatus.PendingReview:
			return "warning"
		case ApplicationStatus.Approved:
			return "success"
		case ApplicationStatus.Denied:
		case ApplicationStatus.Canceled:
			return "danger"
	}
}

export function ApplicationStatusTag({status}: {status: ApplicationStatus}) {
	return <Tag value={startCase(status)} label="Status" className={`is-${applicationStatusTheme(status)}`} />
}

export function AssigneeTag({assignee}: {assignee: Assignee}) {
	return <Tag value={assignee} label="Assignee" className="is-white" />
}

export function CreatedAtTag({createdAt}: {createdAt: Date}) {
	return <Tag value={moment(createdAt).format("L LT")} label="Created At" className="is-white" />
}

export function UpdatedAtTag({updatedAt}: {updatedAt?: Date}) {
	return updatedAt ? <Tag value={moment(updatedAt).format("L LT")} label="Updated At" className="is-white" /> : <></>
}

export function EvaluationOutcomeTag({label, value}: {label: string; value: EvaluationOutcome}) {
	let className

	switch (value) {
		case EvaluationOutcome.Approved:
			className = "is-success"
			break
		case EvaluationOutcome.Denied:
			className = "is-danger"
			break
		case EvaluationOutcome.PendingReview:
			className = "is-warning"
			break
	}

	return <Tag value={startCase(value)} label={label} className={className} />
}
